import { IncomeModel, IncomeType, OwnerOptionsDTO } from './api/useIncomeQuery';
import { IncomeData } from './useIncome';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';

export const CLONE_INCOME_SUCCESS_MESSAGE = 'Income cloned successfully.';

export type IncomeToUpsert =
  | (IncomeModel & { isClone?: boolean })
  | { Id?: string; IncomeType?: IncomeType; Name?: string; isClone?: boolean };

export interface IncomeToDelete {
  id: string;
  name: string;
}

export interface IncomeTableProps {
  incomeType: IncomeType;
  clientId: string;
  data: IncomeData;
  setIncomeToUpsert: (incomeToUpsert: IncomeToUpsert | null) => void;
  setIncomeToDelete: (incomeToDelete: IncomeToDelete | null) => void;
  onClone?: (income: IncomeModel) => void;
  workflowProps?: WorkflowComponentProps;
}

export const ADD_INCOME_SUCCESS_MESSAGE = 'Income added successfully.';
export const EDIT_INCOME_SUCCESS_MESSAGE = 'Income saved successfully.';

export const CLONABLE_INCOME_TYPES = [IncomeType.Salary, IncomeType.Other];

export const isClonableIncomeType = (incomeType: IncomeType | null | undefined) =>
  incomeType !== undefined && incomeType !== null && CLONABLE_INCOME_TYPES.includes(incomeType);

export const getClonedIncomeName = ({
  incomeName,
  incomeTitle,
}: {
  incomeName: string | undefined;
  incomeTitle: string;
}) => {
  return incomeName ? `Cloned - ${incomeName}` : `Cloned - ${incomeTitle}`;
};

export const getIncomeOwnerModel = ({
  value,
  options,
}: {
  value: string;
  options: OwnerOptionsDTO;
}) => {
  const selectedOwnerOption = Object.values(options)
    .map(item => item)
    .flat()
    .find(owner => owner.Name === value);
  return [
    {
      ...selectedOwnerOption,
      Id: selectedOwnerOption.Id,
      Name: selectedOwnerOption.Name,
      OwnerType: selectedOwnerOption.OwnerType,
      Percentage: 1,
    },
  ];
};

const ADVANCED_FIELDS = {
  [IncomeType.Salary]: ['StartAge', 'EndAge', 'Inflation', 'IsTaxFree', 'SelfEmployed'],
  [IncomeType.Business]: ['IncomeGrowth', 'StartAge', 'EndAge', 'SelfEmployed'],
  [IncomeType.RealEstate]: ['IncomeGrowth', 'YearlyTaxableAmount'],
  [IncomeType.SocialSecurity]: [],
  [IncomeType.Other]: ['StartAge', 'EndAge', 'Inflation', 'IsTaxFree'],
};

export const getShowAdvanced = (
  income:
    | IncomeModel
    | {
        IncomeType: IncomeType | null;
        IsBasePlanIncome: boolean;
        Earner: string | null;
        Name: string | null;
      },
) => {
  if (!income) {
    return;
  }
  const advancedFields = income.IncomeType != null ? ADVANCED_FIELDS[income.IncomeType] ?? [] : [];
  const hasAdvancedFieldValue = advancedFields.some(
    field => !!income[field] || income[field] === 0,
  );
  const earmarks = (income as { AccountEarmark?: unknown[] }).AccountEarmark;
  return hasAdvancedFieldValue || (Array.isArray(earmarks) && earmarks.length > 0);
};
